import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { textOf, usePress, verbsFor } from '@/lib/press';
import { answeredForReal, collectPresses, settled } from '@/lib/press-track';
import { useAnnounceSafe } from '@/components/Announcer';

/* shadcn/ui Button, styled with the design reference's .btn classes. */
const buttonVariants = cva('btn', {
  variants: {
    variant: {
      default: '',
      secondary: 'secondary',
      soft: 'soft',
      call: 'call',
      ghost: 'ghost',
    },
    size: { default: '', sm: 'sm' },
  },
  defaultVariants: { variant: 'default', size: 'default' },
});

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * D-1023-BUSY: an `onClick` that returns a promise is a press that calls the server. From the click until it answers
   * the button says what it is doing ("Saving…", "Adding…"), shows a spinner, is `aria-busy`, ignores a second press, and
   * shows "Saved ✓" for a moment. A press the page tracks itself passes `busy`.
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => unknown;
  /** The page's own "working" flag (a mutation, a request it started elsewhere). */
  busy?: boolean;
  /** The words while working; derived from the button's own label when left out ("Add people" → "Adding…"). */
  busyLabel?: string;
  /** The words shown with a check when it answers; derived when left out. */
  doneLabel?: string;
  /** Say the done words in the live region too (leave off where the page already says its outcome). */
  announceDone?: boolean;
  /** The page's own button classes (`big-btn`, `checkin-btn`) instead of `.btn` — for the link pages and the check-in list. */
  plain?: boolean;
  /** Show no done state (the button goes away, or the page changes, when the work is done). */
  noDone?: boolean;
}

/** A handler that returned a promise but has sent nothing yet: the busy look waits this long, so a handler that is only asking a question never flashes it. */
const GRACE_MS = 150;

function isThenable(x: unknown): x is PromiseLike<unknown> {
  return !!x && typeof (x as { then?: unknown }).then === 'function';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, onClick, busy: busyProp, busyLabel, doneLabel, announceDone, noDone, plain, children, disabled, style, 'aria-busy': ariaBusy, ...props }, ref) => {
    const label = props['aria-label'] ?? textOf(children);
    const press = usePress({ label, busyLabel, doneLabel, announceDone, showDone: !noDone });
    const own = React.useRef<HTMLButtonElement | null>(null);
    // the words the button had when it was pressed: if the page gives it new words (Save → Continue), the done state steps aside
    const labelAtPress = React.useRef('');
    const [minWidth, setMinWidth] = React.useState<number | undefined>(undefined);
    const setRef = (el: HTMLButtonElement | null) => {
      own.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = el;
    };
    const Comp = asChild ? Slot : 'button';

    const working = !!busyProp || press.busy;
    const done = !working && press.phase === 'done' && labelAtPress.current === label;
    const words = press.busy ? press.text : busyProp ? (busyLabel ?? verbsFor(label).busy) : '';
    // a press the page tracks itself is said once when it starts (a press this button runs is said by usePress)
    const announce = useAnnounceSafe();
    const wasProp = React.useRef(false);
    React.useEffect(() => {
      if (busyProp && !wasProp.current && !press.busy) announce(words);
      wasProp.current = !!busyProp;
    }, [busyProp, press.busy, words, announce]);
    if (asChild) return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} onClick={onClick as React.MouseEventHandler | undefined} disabled={disabled} aria-busy={ariaBusy} style={style} {...props}>{children}</Comp>;
    const handle = (e: React.MouseEvent<HTMLButtonElement>) => {
      // a press that is out is not pressed again — not by the mouse, not by Enter in a field that submits this button
      if (working || press.flying()) {
        e.preventDefault();
        return;
      }
      if (!onClick) return;
      const keep = own.current?.offsetWidth;
      labelAtPress.current = label;
      const { result: r, sent } = collectPresses(() => onClick(e));
      if (isThenable(r) || sent.length) {
        if (keep) setMinWidth(keep);
        // stay busy until what the handler returned has answered and so has every press it sent; a refusal the page
        // has already said in its own words only brings the button back
        void press.run(async () => {
          const value = isThenable(r) ? await r : undefined;
          await settled(sent);
          return value;
        }, { label, graceMs: sent.length ? 0 : GRACE_MS, showDone: () => !noDone && answeredForReal(sent) });
      }
    };
    const isIconOnly = !textOf(children).trim();
    return (
      <Comp
        className={cn(plain ? undefined : buttonVariants({ variant, size }), working && 'is-busy', className)}
        ref={setRef}
        type={type ?? 'button'}
        onClick={handle}
        // while working the button stays focusable (aria-disabled), so a screen reader keeps its place
        disabled={working ? undefined : disabled}
        aria-disabled={working || undefined}
        aria-busy={working || ariaBusy || undefined}
        style={(working || done) && minWidth ? { minWidth, ...style } : style}
        {...props}
        aria-label={working && props['aria-label'] ? words || props['aria-label'] : props['aria-label']}
      >
        {working ? (
          <>
            <span className="busy-spin" aria-hidden="true" />
            {isIconOnly ? <span className="sr-only">{words}</span> : <span className="busy-words">{words}</span>}
          </>
        ) : done && press.text ? (
          <span className="busy-done" data-testid="busy-done">
            {press.text} <span aria-hidden="true">✓</span>
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
